import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useAuth } from '../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const EVENT_TYPE_SEARCH_FIELDS = ['name', 'description'];
const EVENT_TYPE_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (t) => t.name?.toLowerCase() ?? '' },
];



const EventTypeComponent = () => {
    const [eventTypes, setEventTypes] = useState([]);
    const [currentEventType, setCurrentEventType] = useState({ name: '', description: '' });
    const [showForm, setShowForm] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [eventTypeToDelete, setEventTypeToDelete] = useState(null);
    const { authState } = useAuth();
    const navigate = useNavigate();

    const {
        items: visibleEventTypes,
        resultCount,
        totalCount,
        isFiltered,
        search,
        sort,
        clear,
    } = useListControls(eventTypes, {
        searchFields: EVENT_TYPE_SEARCH_FIELDS,
        sortOptions: EVENT_TYPE_SORT_OPTIONS,
    });

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!authState) {
            navigate('/login');
        }
    }, [authState, navigate]);

    // Fetch all event types
    useEffect(() => {
        fetchEventTypes();
    }, []);

    const fetchEventTypes = async () => {
        const response = await fetch(`${backendURL}/api/event-types`, {
            method: 'GET',
            credentials: 'include'
        });
        const data = await response.json();
        setEventTypes(data);
    };

    const handleFormChange = (e) => {
        setCurrentEventType({ ...currentEventType, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (currentEventType.event_type_id) {
            await updateEventType(currentEventType.event_type_id, currentEventType);
        } else {
            await createEventType(currentEventType);
        }
        setCurrentEventType({ name: '', description: '' });
        setShowForm(false);
        fetchEventTypes();
    };

    const createEventType = async (eventTypeData) => {
        await fetch(`${backendURL}/api/event-types`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(eventTypeData),
            credentials: 'include'
        });
    };

    const updateEventType = async (eventTypeId, eventTypeData) => {
        await fetch(`${backendURL}/api/event-types/${eventTypeId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(eventTypeData),
            credentials: 'include'
        });
    };

    const deleteEventType = async () => {
        if (eventTypeToDelete) {
            await fetch(`${backendURL}/api/event-types/${eventTypeToDelete}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            setEventTypeToDelete(null);
            setShowModal(false);
            fetchEventTypes();
        }
    };

    const editEventType = (eventType) => {
        setCurrentEventType(eventType);
        setShowForm(true);
    };

    const handleCancel = () => {
        setCurrentEventType({ name: '', description: '' });
        setShowForm(false);
    };

    const handleToggleForm = () => {
        if (!showForm) {
            setCurrentEventType({ name: '', description: '' });
        }
        setShowForm(!showForm);
    };

    const eventTypeForm = (
        <Form onSubmit={handleSubmit}>
            <FormGrid>
                <Form.Group controlId="name">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter event type name"
                        name="name"
                        onChange={handleFormChange}
                        value={currentEventType.name}
                    />
                </Form.Group>
                <Form.Group controlId="description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        placeholder="Enter event type description"
                        name="description"
                        onChange={handleFormChange}
                        value={currentEventType.description}
                    />
                </Form.Group>
                <FormActions>
                    <Button type="submit">Submit</Button>
                    <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                </FormActions>
            </FormGrid>
        </Form>
    );

    return (
        <div>
            <CrudListShell
                addLabel="Add New Event Type"
                listLabel="Show Event Types List"
                showForm={showForm}
                onToggleForm={handleToggleForm}
                tableHeading="Event Types"
                form={eventTypeForm}
                filters={(
                    <FilterBar
                        summary={(
                            <ResultSummary
                                count={resultCount}
                                total={totalCount}
                                noun="event type"
                                onClear={clear}
                                isFiltered={isFiltered}
                            />
                        )}
                    >
                        <SearchInput label="Search event types" placeholder="Name or description" {...search} />
                        <SortControl {...sort} />
                    </FilterBar>
                )}
                emptyState={(
                    <tr>
                        <td>
                            <ScheduleEmptyState>No event types have been added yet.</ScheduleEmptyState>
                        </td>
                    </tr>
                )}
            >
                {visibleEventTypes.map((eventType) => (
                    <ExpandableRow
                        key={eventType.event_type_id}
                        main={{ title: eventType.name, subtitle: eventType.description || 'No description provided.' }}
                        actions={(
                            <>
                                <Button size="sm" onClick={() => editEventType(eventType)} aria-label="Edit event type">
                                    <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => {
                                        setEventTypeToDelete(eventType.event_type_id);
                                        setShowModal(true);
                                    }}
                                    aria-label="Delete event type"
                                >
                                    <i className="bi bi-trash3"></i>
                                </Button>
                            </>
                        )}
                    />
                ))}
            </CrudListShell>

            <ConfirmDeleteModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onConfirm={deleteEventType}
                bodyText="Are you sure you want to delete this event type?"
            />
        </div>
    );
};

export default EventTypeComponent;
